import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@/store/authStore';
import { Calendar, Clock, Filter, Search, User, ChevronRight, ChevronDown, Check, ChevronsUpDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

export default function PersonReportPage() {
    const { currentOrg, projects, fetchProjects, fetchMemberSummary, fetchOrgMembers } = useAppStore();
    const { user } = useAuthStore();
    const [report, setReport] = useState(null);
    const [selectedProject, setSelectedProject] = useState('all');
    const [selectedUser, setSelectedUser] = useState(user?.id || '');

    // Default dates: Today and 7 days ago
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

    const [expandedUsers, setExpandedUsers] = useState({});
    const [orgMembers, setOrgMembers] = useState([]);

    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);

    useEffect(() => {
        if (currentOrg) {
            fetchProjects(currentOrg.id);
            fetchOrgMembers(currentOrg.id).then(setOrgMembers);
        }
    }, [currentOrg, fetchProjects, fetchOrgMembers]);

    const loadReport = useCallback(async () => {
        if (!currentOrg) return;
        setReport(null);
        try {
            const filters = {};
            if (selectedProject !== 'all') filters.projectId = selectedProject;
            if (selectedUser !== 'all') filters.userId = selectedUser;
            if (startDate) filters.startDate = startDate;
            if (endDate) filters.endDate = endDate;
            const data = await fetchMemberSummary(currentOrg.id, filters);
            setReport(data);
        } catch (err) {
            console.error('Failed to load report:', err);
        }
    }, [currentOrg, selectedProject, selectedUser, startDate, endDate, fetchMemberSummary]);

    useEffect(() => {
        if (currentOrg) {
            loadReport();
        }
    }, [currentOrg, loadReport]);

    useEffect(() => {
        if (report && Object.keys(report).length === 1) {
            setExpandedUsers({ [Object.keys(report)[0]]: true });
        }
    }, [report]);

    const toggleUserExpanded = (uid) => {
        setExpandedUsers(prev => ({ ...prev, [uid]: !prev[uid] }));
    };

    // Filter projects based on selected user
    const availableProjects = useMemo(() => {
        if (selectedUser === 'all') return projects;
        return projects.filter(p => p.members?.some(m => m.user_id === selectedUser));
    }, [selectedUser, projects]);

    // Handle user change - reset project if no longer valid
    const handleUserSelect = (uid) => {
        setSelectedUser(uid);
        setUserDropdownOpen(false);

        // If a project was selected, check if it's still valid for this user
        if (selectedProject !== 'all' && uid !== 'all') {
            const isStillValid = projects.find(p => p.id === selectedProject)?.members?.some(m => m.user_id === uid);
            if (!isStillValid) {
                setSelectedProject('all');
            }
        }
    };

    if (!currentOrg) {
        return (
            <div className="p-8 flex items-center justify-center h-full">
                <p className="text-zinc-400">Select an organization to view reports</p>
            </div>
        );
    }

    const selectedUserName = selectedUser === 'all' ? 'All Members' : orgMembers.find(m => m.user_id === selectedUser)?.user?.name || 'Selected Member';
    const selectedProjectName = selectedProject === 'all' ? 'All Projects' : projects.find(p => p.id === selectedProject)?.name || 'Selected Project';

    return (
        <div className="p-6 md:p-8" data-testid="person-report-page">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white font-['Manrope']">Person's Report</h1>
                    <p className="text-zinc-400 text-sm mt-1">
                        {selectedUser === 'all' ? 'Holistic view of team performance' : `Detailed performance report for ${selectedUserName}`}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Search Member</label>
                    <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={userDropdownOpen}
                                className="w-full justify-between bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 hover:text-white h-10 transition-colors"
                            >
                                <div className="flex items-center gap-2 truncate">
                                    <User className="w-4 h-4 text-zinc-500" />
                                    {selectedUserName}
                                </div>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-zinc-900 border-zinc-800 overflow-hidden">
                            <Command className="bg-zinc-900">
                                <CommandInput placeholder="Search member..." className="h-10" />
                                <CommandList className="max-h-[300px]">
                                    <CommandEmpty>No member found.</CommandEmpty>
                                    <CommandGroup>
                                        {orgMembers.map((member) => (
                                            <CommandItem
                                                key={member.user_id}
                                                value={`${member.user?.name} ${member.user?.email}`}
                                                onSelect={() => handleUserSelect(member.user_id)}
                                                className="text-zinc-200 cursor-pointer"
                                            >
                                                <Check className={cn("mr-2 h-4 w-4", selectedUser === member.user_id ? "opacity-100" : "opacity-0")} />
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{member.user?.name}</span>
                                                    <span className="text-xs text-zinc-500">{member.user?.email}</span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Project</label>
                    <Popover open={projectDropdownOpen} onOpenChange={setProjectDropdownOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={projectDropdownOpen}
                                className="w-full justify-between bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 hover:text-white h-10 transition-colors"
                            >
                                <div className="flex items-center gap-2 truncate">
                                    <Filter className="w-4 h-4 text-zinc-500" />
                                    {selectedProjectName}
                                </div>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-zinc-900 border-zinc-800 overflow-hidden">
                            <Command className="bg-zinc-900">
                                <CommandInput placeholder="Search project..." className="h-10" />
                                <CommandList className="max-h-[300px]">
                                    <CommandEmpty>No project found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="all"
                                            onSelect={() => {
                                                setSelectedProject('all');
                                                setProjectDropdownOpen(false);
                                            }}
                                            className="text-zinc-200 cursor-pointer"
                                        >
                                            <Check className={cn("mr-2 h-4 w-4", selectedProject === "all" ? "opacity-100" : "opacity-0")} />
                                            All Projects
                                        </CommandItem>
                                        {availableProjects.map((p) => (
                                            <CommandItem
                                                key={p.id}
                                                value={p.name}
                                                onSelect={() => {
                                                    setSelectedProject(p.id);
                                                    setProjectDropdownOpen(false);
                                                }}
                                                className="text-zinc-200 cursor-pointer"
                                            >
                                                <Check className={cn("mr-2 h-4 w-4", selectedProject === p.id ? "opacity-100" : "opacity-0")} />
                                                {p.name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Start Date</label>
                    <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="bg-zinc-900 border-zinc-800 h-10 [color-scheme:dark] text-zinc-200 focus:ring-cyan-500/20 focus:border-cyan-500/50"
                    />
                </div>
                <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">End Date</label>
                    <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="bg-zinc-900 border-zinc-800 h-10 [color-scheme:dark] text-zinc-200 focus:ring-cyan-500/20 focus:border-cyan-500/50"
                    />
                </div>
            </div>

            {/* Main Content: Detailed Report for Selected Person */}
            <div className="space-y-6">
                {report && Object.keys(report).length > 0 ? (
                    (() => {
                        const member = Object.values(report)[0];
                        const isWorkingNow = Object.entries(member.projects).some(([pid, p]) => {
                            const projectObj = projects.find(proj => proj.id === pid || proj.name === p.name);
                            return Object.values(p.tasks).some(t => {
                                const isTaskInProgress = projectObj?.statuses?.find(s => s.id === t.status)?.is_in_progress;
                                return t.is_running || isTaskInProgress;
                            });
                        });
                        const isTimerActive = Object.values(member.projects).some(p =>
                            Object.values(p.tasks).some(t => t.is_running)
                        );

                        return (
                            <div className="animate-in fade-in duration-500">
                                {/* Member Header & Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                    <div className="md:col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex items-center justify-between shadow-xl">
                                        <div className="flex items-center gap-5">
                                            <Avatar className="w-16 h-16 border-2 border-zinc-800 shadow-2xl">
                                                <AvatarFallback className="bg-gradient-to-br from-cyan-500/20 to-violet-500/20 text-cyan-400 font-bold text-2xl">
                                                    {member.name.charAt(0).toUpperCase()}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <div className="flex items-center gap-3 mb-1">
                                                    <h2 className="text-2xl font-bold text-white tracking-tight">{member.name}</h2>
                                                    {isWorkingNow && (
                                                        <Badge className={cn(
                                                            "bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-[10px] py-0 h-5 px-2",
                                                            isTimerActive && "animate-pulse"
                                                        )}>
                                                            {isTimerActive ? "LIVE TRACKING" : "ACTIVE NOW"}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-zinc-500 font-medium">{member.email}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-xl flex flex-col justify-center">
                                        <p className="text-[11px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-2">Total Effort</p>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-4xl font-bold text-white font-['JetBrains_Mono']">
                                                {formatTime(member.total_seconds)}
                                            </span>
                                            <div className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)] animate-pulse" />
                                        </div>
                                    </div>
                                </div>

                                {/* Projects Breakdown */}
                                <div className="space-y-8">
                                    {Object.entries(member.projects).map(([pid, project]) => (
                                        <div key={pid} className="space-y-4">
                                            <div className="flex items-center justify-between border-b border-zinc-800/50 pb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-1 h-4 bg-cyan-500 rounded-full" />
                                                    <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-[0.15em]">{project.name}</h3>
                                                </div>
                                                <Badge variant="ghost" className="text-zinc-500 font-['JetBrains_Mono'] text-xs">
                                                    {formatTime(project.total_seconds)}
                                                </Badge>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                                {Object.entries(project.tasks).map(([tid, task]) => {
                                                    const projectObj = projects.find(pr => pr.id === pid || pr.name === project.name);
                                                    const isTaskInProgress = projectObj?.statuses?.find(s => s.id === task.status)?.is_in_progress;
                                                    const showLive = task.is_running;
                                                    const isWorking = showLive || isTaskInProgress;

                                                    return (
                                                        <div key={tid} className={cn(
                                                            "p-4 rounded-xl border transition-all duration-300 flex flex-col justify-between group",
                                                            isWorking
                                                                ? "bg-cyan-500/5 border-cyan-500/30 shadow-[0_4px_20px_rgba(6,182,212,0.05)]"
                                                                : "bg-zinc-900/30 border-zinc-800/80 hover:border-zinc-700 hover:bg-zinc-800/20"
                                                        )}>
                                                            <div className="flex items-start justify-between gap-3 mb-4">
                                                                <p className="text-sm font-medium text-zinc-300 group-hover:text-white transition-colors line-clamp-2">
                                                                    {task.title}
                                                                </p>
                                                                {isWorking && (
                                                                    <div className="flex items-center gap-1 shrink-0">
                                                                        <span className={cn(
                                                                            "w-1.5 h-1.5 rounded-full bg-cyan-400",
                                                                            showLive && "animate-pulse"
                                                                        )} />
                                                                        <span className="text-[10px] font-bold text-cyan-400 tracking-tighter uppercase whitespace-nowrap">
                                                                            {showLive ? 'Live' : 'Working'}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-auto">
                                                                <Clock className={cn("w-3.5 h-3.5", isWorking ? "text-cyan-400" : "text-zinc-500")} />
                                                                <span className={cn(
                                                                    "text-sm font-bold font-['JetBrains_Mono']",
                                                                    isWorking ? "text-white" : "text-zinc-400"
                                                                )}>
                                                                    {formatTime(task.total_seconds)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()
                ) : (
                    <div className="bg-zinc-900/50 border border-zinc-800 border-dashed rounded-2xl p-20 text-center animate-in fade-in zoom-in duration-300">
                        <div className="w-16 h-16 bg-zinc-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <User className="w-8 h-8 text-zinc-600" />
                        </div>
                        <h3 className="text-white font-semibold mb-1">No Activity Found</h3>
                        <p className="text-zinc-500 max-w-xs mx-auto">We couldn't find any time logs for the selected criteria and date range.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
